import axios from "axios";
import { AuthStore } from "../auth/authStore";

/**
 * ===============================
 * AXIOS BASE INSTANCE (PRODUCTION)
 * ===============================
 */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:5003";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // ⏱ prevent hanging request
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * ===============================
 * REQUEST INTERCEPTOR
 * - attach JWT
 * - normalize headers
 * ===============================
 */
api.interceptors.request.use(
  (config) => {
    const token = AuthStore.getToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Jangan override multipart header
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * ===============================
 * RESPONSE INTERCEPTOR
 * - global error handling
 * - auto logout on auth failure
 * ===============================
 */

let isLoggingOut = false;

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // ===== NO RESPONSE (NETWORK / CORS / SERVER DOWN)
    if (!error.response) {
      console.error("[API] Network error:", error);
      return Promise.reject({
        message: "Network error. Please check your connection.",
      });
    }

    const { status, config } = error.response;

    // ===== UNAUTHORIZED (TOKEN EXPIRED / INVALID)
    if (status === 401 && !config.__isRetryRequest) {
      if (!isLoggingOut) {
        isLoggingOut = true;

        AuthStore.logout();

        // hard redirect to clear state
        window.location.replace("/login");
      }
    }

    // ===== FORBIDDEN (ROLE ISSUE)
    if (status === 403) {
      console.warn("[API] Forbidden access");
    }

    // ===== SERVER ERROR
    if (status >= 500) {
      console.error("[API] Server error:", error.response.data);
    }

    return Promise.reject(error);
  }
);

export default api;
